import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Space, Table, Input, TableColumnType } from "antd";
import { DeleteOutlined, DownloadOutlined, CloudUploadOutlined, CloudDownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { AuthContext } from "../../../Contexts/AuthContext";
import { CompanyContext } from "../../../Contexts/CompanyContext";
import { FilePleaTechnicalModel } from "../../../Models/FilePleaTechnicalModel";
import { GetAllFilesPleaByPleaId, UploadPlea, DownloadFileAPI, DeleteFilePlea } from "../../../API/RegisterAPI";
import { IResponseProps } from "../../../Interfaces/IResponseProps";
import { toast } from "react-toastify";
import useModal from "../../../Hooks/useModal";
import CardHeader from "../../../antd/components/CardHeader";
import { AllUploadModal } from "../../../Components/Shared/UploadModal/AllUploadModal";
import Loader from "../../../Components/Shared/Loader";
import ModalDelete from "../../../Components/Shared/ModalDelete";
import type { ColumnType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { IoCodeSlash } from "react-icons/io5";

export default function TechnicalPlea() {
  const { userEmail } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const { id, pleaId } = useParams();
  const url = `${company.id}/plea/${pleaId}`;
  const [fileList, setFileList] = useState<FilePleaTechnicalModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSend, setIsSend] = useState<string>("");
  const [file, setFile] = useState<Blob>();
  const [itemToDelete, setItemToDelete] = useState<string>("");
  const [deleteResponse, setDeleteResponse] = useState(false);
  const { showModal: isShowingExcelModal, toggleShowModal: toggleExcelModal } = useModal();
  const { showModal: showModalDelete, toggleShowModal: toggleShowModalDelete } = useModal();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const getColumnSearchProps = (dataIndex: keyof FilePleaTechnicalModel): ColumnType<FilePleaTechnicalModel> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes((value as string).toLowerCase()),
    sorter: (a, b) => {
      if (typeof a[dataIndex] === 'number' && typeof b[dataIndex] === 'number') {
        return (a[dataIndex] as number) - (b[dataIndex] as number);
      }
      return String(a[dataIndex]).localeCompare(String(b[dataIndex]));
    },
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const columns: ColumnType<FilePleaTechnicalModel>[] = [
    {
      title: 'Versão',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      ...getColumnSearchProps('version'),
      sorter: (a: any, b: any) => (a.version || 0) - (b.version || 0),
    },
    {
      title: 'Arquivo',
      dataIndex: 'fileName',
      key: 'fileName',
      ...getColumnSearchProps('fileName'),
    },
    {
      title: 'Usuário',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ...getColumnSearchProps('createdBy'),
    },
    {
      title: 'Envio ou Recebimento',
      key: 'isSend',
      render: () => isSend,
      filters: [
        { text: 'Enviado', value: 'Enviado' },
        { text: 'Recebido', value: 'Recebido' },
      ],
      onFilter: (value: any, record) => isSend.includes(value),
    },
    {
      title: 'Data de Upload',
      dataIndex: 'created',
      key: 'created',
      ...getColumnSearchProps('created'),
      sorter: (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<CloudDownloadOutlined style={{ color: '#0958D9' }} />}
            onClick={() => downloadFileById(record.id, record.fileName)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setItemToDelete(record.id);
              toggleShowModalDelete();
            }}
          />
        </Space>
      ),
    },
  ];

  const tableComponents = {
    header: {
      cell: (props: React.HTMLAttributes<HTMLElement>) => (
        <th {...props} style={{ ...props.style, border: 'none', borderBottom: '1px solid #f0f0f0', padding: '12px 16px', background: '#fafafa' }} />
      ),
    },
    body: {
      cell: (props: React.HTMLAttributes<HTMLElement>) => (
        <td {...props} style={{ ...props.style, border: 'none', borderBottom: '1px solid #f0f0f0', padding: '12px 16px', }} />
      ),
    },
  };

  useEffect(() => {
    if (deleteResponse && itemToDelete) {
      const fileToDelete = fileList.find(file => file.id === itemToDelete);
      if (fileToDelete) {
        deleteFile(fileToDelete.fileName, fileToDelete.id);
      }
      setItemToDelete("");
      setDeleteResponse(false);
    }
  }, [deleteResponse]);

  const getAllFiles = async () => {
    try {
      IsItReceivedOrSent();
      if (pleaId) {
        const resp = await GetAllFilesPleaByPleaId(pleaId);
        if (resp.statusCode === 200) {
          setFileList(resp.data);
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const deleteFile = async (fileName: string, fileId: string) => {
    try {
      setLoading(true);
      if (pleaId) {
        const resp = await DeleteFilePlea(company.id, encodeURIComponent(fileName), fileId, pleaId);
        if (resp.statusCode === 200) {
          toast.success("Arquivo deletado com sucesso");
          getAllFiles();
        }
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadFileById = async (id: string, fileName: string) => {
    try {
      const blobResp = await DownloadFileAPI(id);
      const blob = new Blob([blobResp], { type: 'application/octet-stream' });
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const sendFilePlea = async () => {
    toggleExcelModal();
    try {
      setLoading(true);
      if (pleaId) {
        await UploadPlea(file, url, userEmail, pleaId);
        toast.success("Upload feito com sucesso!");
        getAllFiles();
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  function IsItReceivedOrSent() {
    setIsSend(userEmail.includes("fi-group.com") ? "Enviado" : "Recebido");
  }
  useEffect(() => {
    getAllFiles();
  }, []);

  return (
    <>
      {loading && <Loader />}
      <ModalDelete
        show={showModalDelete}
        toggleShow={toggleShowModalDelete}
        setResponse={setDeleteResponse}
      />
      <Card
        style={{
          margin: '0 2px',
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02)'
        }}
        title={<CardHeader
          pageName={"Pleito Técnico"}
          hasPageBefore={true}
          pageBeforeName={'Cadastro'}
          linkPageBefore={`/plea/financial/finep/${id}/${pleaId}`}
        />}
        bodyStyle={{ padding: '16px' }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px',
          gap: '8px'
        }}>
          <Button
            icon={<CloudDownloadOutlined />}
            style={{ borderRadius: '6px' }}
          >
            Download
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            style={{ borderRadius: '6px' }}
            onClick={toggleExcelModal}
          >
            Upload
          </Button>
        </div>
        <div style={{ marginTop: '0px' }}>
          <Table
            columns={columns}
            dataSource={fileList}
            rowKey="id"
            pagination={false}
            bordered={false}
            scroll={{ x: true }}
            style={{ border: 'none' }}
            components={tableComponents}
          />
        </div>
      </Card>
      {isShowingExcelModal && (
        <AllUploadModal
          action={sendFilePlea}
          icon={IoCodeSlash}
          isShowing={isShowingExcelModal}
          toggle={toggleExcelModal}
          setFile={setFile}
        />
      )}
    </>
  );
}